import { CollectionUtils } from "utilities/collection-utils";
import { ImageTagAttr } from "utilities/hooks/quill/use-quill-image-tag-sanitizer";
import StringUtils from "utilities/string-utils";

// -------------------------------------------------------------------------------------------------
// #region Private Functions
// -------------------------------------------------------------------------------------------------

const parse = (html?: string): DocumentFragment | undefined => {
    html = html?.trim();

    if (StringUtils.hasValue(html)) {
        return getTemplateContent(html);
    }

    return undefined;
};

const getTemplateContent = (html: string): DocumentFragment => {
    const fragment = document.createDocumentFragment();
    const temp = document.createElement("div");
    temp.innerHTML = html;

    while (temp.firstChild) {
        fragment.appendChild(temp.firstChild);
    }

    return fragment;
};

const hasDataAttribute = (img: HTMLImageElement, dataName: string): boolean => {
    return StringUtils.hasValue(img.attributes.getNamedItem(dataName)?.value);
};

const hasInternalFileDataAttributes = (img: HTMLImageElement): boolean => {
    return (
        hasDataAttribute(img, ImageTagAttr.DataStorageContainer) &&
        hasDataAttribute(img, ImageTagAttr.DataRelativeProviderPath)
    );
};

const imageHasValidAttributes = (img: HTMLImageElement): boolean => {
    return (
        StringUtils.hasValue(img.src) ||
        hasDataAttribute(img, ImageTagAttr.DataSrc) ||
        hasInternalFileDataAttributes(img)
    );
};

// #endregion Private Functions

// -------------------------------------------------------------------------------------------------
// #region Functions
// -------------------------------------------------------------------------------------------------

const isEmpty = (value?: string) => {
    const container = parse(value);
    if (container == null) {
        return true;
    }

    // check for image content, regardless of text content.
    const images = Array.from(container.querySelectorAll("img") ?? []);
    const validImages = images.filter(imageHasValidAttributes);

    if (CollectionUtils.hasValues(validImages)) {
        return false;
    }

    // if no images, check for text content
    const textContent = container.textContent;
    return StringUtils.isEmpty(textContent ?? "");
};

const hasValue = (value?: string): value is string => !isEmpty(value);

// #endregion Functions

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

const RichTextUtils = {
    isEmpty,
    hasValue,
    parse,
};

export default RichTextUtils;

// #endregion Exports
